import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {WMSContext} from './WMSContext';
import useIsMobile from "hooks/useIsMobile";
import {WmsItemsStatus} from "../../../utils/WmsItemsStatus";

// some state can be replaced by SidebarContext
const WMSContextProvider = ({ children, member, authorizations, organization, history }) => {
    const [currentInventory, setCurrentInventory] = useState(null);
    const [currentMember, setCurrentMember] = useState(null);
    const [currentMemberAuthorizations, setCurrentMemberAuthorizations] = useState(null);
    const [currentOrganization, setCurrentOrganization] = useState(null);
    const [currentTask, setCurrentTask] = useState(null);
    const [preferredPrinter, setPreferredPrinter] = useState({});
    const [preferredPackingStation, setPreferredPackingStation] = useState({});
    const isMobile = useIsMobile();

    useEffect(() => {
        let preferredPrinterString = localStorage[organization?.id + '_preferredPrinters'];
        if (preferredPrinterString) {
            setPreferredPrinter({
                labelPrinterId: JSON.parse(preferredPrinterString).labelPrinterId,
                documentPrinterId: JSON.parse(preferredPrinterString).documentPrinterId,
                organizationId: organization?.id
            });
        } else {
            // No stored printer preference for this organization: never keep a printer
            // preference (and its organizationId) from a previously loaded organization,
            // otherwise a later waybill print request is sent with a mismatched
            // organizationId and silently rejected by the backend.
            setPreferredPrinter({organizationId: organization?.id});
        }
        setCurrentMember(member);
        setCurrentMemberAuthorizations(authorizations);
        setCurrentInventory(member?.inventories?.[0]);
        setCurrentOrganization(organization);
    }, [member, authorizations, organization]);

    const getWmsItemSponsoredPrice = (item) => {
        const sponsoredAdjustments = (item.lineItem?.adjustments ?? [])
            .filter(adjustment => adjustment.sponsored)
            .reduce((total, adjustment) => total + adjustment.amount, 0);

        return (item.lineItem?.amount ?? 0) - sponsoredAdjustments / 100;
    }

    const getItemPriceWithCommission = (item, price) => {
        const commissionRate = Number(item.lineItem?.retailer?.commissionRate ?? 0) / 100;

        return price - price * commissionRate;
    }

    const getItemTotalPrice = (item, status = [WmsItemsStatus.PICKING, WmsItemsStatus.PENDING]) => {
        if (!status.some((s) => s === item.status)) {
            return 0
        }

        return getItemPriceWithCommission(item, getWmsItemSponsoredPrice(item))
    }

    const groupWmsItemsBySku = (wmsItems) => {
        let groupedItems = [];
        wmsItems.forEach((item) => {
            const key = item.lineItem.variationSku;
            if (!groupedItems.some(i => i.lineItem.variationSku === key)) {
                item.count = 1;
                item.checked = false;
                item.totalPrice = getItemTotalPrice(item) ?? 0
                groupedItems.push(item);
            } else {
                groupedItems[groupedItems.findIndex(i => i.lineItem.variationSku === key)].count += 1;
                groupedItems[groupedItems.findIndex(i => i.lineItem.variationSku === key)].totalPrice += getItemTotalPrice(item);
            }
        });
        return groupedItems;
    };

    // Simple redirect function so that the chosen link is selected in the sidebar
    const redirect = (path) => history.push(path);

    return (
        <WMSContext.Provider value={{
            currentMemberAuthorizations,
            currentMember,
            currentInventory,
            currentOrganization,
            currentTask,
            preferredPrinter,
            preferredPackingStation,
            isMobile,
            setPreferredPrinter,
            setPreferredPackingStation,
            setCurrentTask,
            groupWmsItemsBySku,
            getItemTotalPrice,
            redirect
        }}>
            {children}
        </WMSContext.Provider>
    );
};

const mapStateToProps = state => ({
    authorizations: state.authentication.authorizations,
    member: state.authentication.member,
    organization: state.currentOrganization.retrieved
});

export default connect(mapStateToProps)(WMSContextProvider);
